import { useState } from "react";
import type { AbsenceDTO, StaffMemberDTO } from "@easyshop/shared";
import { ABSENCE_TYPE_LABELS, ABSENCE_TYPES } from "@easyshop/shared";
import { Button, Drawer, DrawerHeader, ErrorState, FieldLabel, Pill, inputClass } from "@/components/ui";
import { api } from "@/lib/api";
import { useInvalidatePlanning } from "@/lib/queries";
import { dateShort, ymd } from "./utils";
import { ImportEquipe } from "./ImportEquipe";

const COLORS = ["#2f7d4f", "#6b4fa0", "#b86a1f", "#1f6fb8", "#b83f5e", "#4f8a8b"];

type Form = {
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
  contractHours: string;
  color: string;
};

const empty: Form = { name: "", jobTitle: "Vendeur·se", email: "", phone: "", contractHours: "35", color: COLORS[0] };

/**
 * Équipe d'une boutique : fiches (liées ou non à un compte pour voir ses
 * horaires), heures au contrat, absences.
 */
export function TeamDrawer({
  open,
  onClose,
  boutiqueId,
  city,
  staff,
  absences,
}: {
  open: boolean;
  onClose: () => void;
  boutiqueId: string;
  city: string;
  staff: StaffMemberDTO[];
  absences: AbsenceDTO[];
}) {
  const invalidate = useInvalidatePlanning();
  const [editing, setEditing] = useState<StaffMemberDTO | "new" | null>(null);
  const [importing, setImporting] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [absence, setAbsence] = useState({ type: "LEAVE" as (typeof ABSENCE_TYPES)[number], startDate: ymd(new Date()), endDate: ymd(new Date()) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const team = staff.filter((s) => s.boutiqueId === boutiqueId);

  const open_ = (s: StaffMemberDTO | "new") => {
    setError(null);
    setEditing(s);
    setForm(
      s === "new"
        ? { ...empty, color: COLORS[team.length % COLORS.length] }
        : {
            name: s.name,
            jobTitle: s.jobTitle ?? "",
            email: s.email ?? "",
            phone: s.phone ?? "",
            contractHours: s.contractHours != null ? String(s.contractHours) : "",
            color: s.color ?? COLORS[0],
          }
    );
  };

  const run = async (fn: () => Promise<unknown>, close = true) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await invalidate();
      if (close) setEditing(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(() => {
      const body = {
        boutiqueId,
        name: form.name,
        jobTitle: form.jobTitle || null,
        email: form.email || null,
        phone: form.phone || null,
        contractHours: form.contractHours === "" ? null : Number(form.contractHours),
        color: form.color,
        linkAccount: true,
      };
      return editing === "new" ? api.post("/staff", body) : api.patch(`/staff/${(editing as StaffMemberDTO).id}`, body);
    });

  const current = editing && editing !== "new" ? editing : null;
  const myAbsences = current ? absences.filter((a) => a.staffMemberId === current.id) : [];

  return (
    <Drawer open={open} onClose={() => (setEditing(null), onClose())} width={440}>
      <DrawerHeader
        title={editing ? (editing === "new" ? "Nouvelle personne" : editing.name) : importing ? `Importer l'équipe — ${city}` : `Équipe — ${city}`}
        onClose={() => (setEditing(null), setImporting(false), onClose())}
      />
      {!editing && importing && (
        <ImportEquipe
          boutiqueId={boutiqueId}
          onCancel={() => setImporting(false)}
          onDone={(n) => {
            setImporting(false);
            setInfo(`${n} personne${n > 1 ? "s" : ""} ajoutée${n > 1 ? "s" : ""} à l'équipe.`);
            void invalidate();
          }}
        />
      )}
      {!editing && !importing && (
        <>
          {info && <div className="rounded-card-sm bg-accent-soft px-4 py-2 text-[12.5px] font-semibold">{info}</div>}
          <div className="flex flex-col">
            {team.length === 0 && <div className="py-4 text-[13px] text-muted">Aucune personne dans l'équipe.</div>}
            {team.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => open_(s)}
                className="flex items-center gap-3 border-b border-line-row py-3 text-left hover:bg-page"
              >
                <span className="h-3 w-3 flex-shrink-0 rounded-full" style={{ background: s.color ?? "var(--accent)" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold">
                    {s.name} {!s.active && <span className="font-normal text-muted">(inactive)</span>}
                  </div>
                  <div className="text-[11.5px] text-muted-2">
                    {s.jobTitle ?? "—"}
                    {s.contractHours != null ? ` · ${s.contractHours} h/sem.` : ""}
                    {s.linkedUser ? " · voit ses horaires" : ""}
                  </div>
                </div>
                <span className="text-[12px] text-accent">Modifier</span>
              </button>
            ))}
          </div>
          <Button onClick={() => open_("new")}>+ Ajouter une personne</Button>
          <Button variant="outline" onClick={() => (setInfo(null), setImporting(true))}>
            Importer depuis la caisse
          </Button>
        </>
      )}
      {editing && (
        <>
          <div>
            <FieldLabel>Nom</FieldLabel>
            <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="flex gap-[10px]">
            <div className="flex-1">
              <FieldLabel>Poste</FieldLabel>
              <input className={inputClass} value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
            </div>
            <div className="w-[120px]">
              <FieldLabel>Heures / sem.</FieldLabel>
              <input type="number" min={0} max={80} className={inputClass} value={form.contractHours} onChange={(e) => setForm({ ...form, contractHours: e.target.value })} />
            </div>
          </div>
          <div>
            <FieldLabel>E-mail</FieldLabel>
            <input type="email" className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <div className="mt-1 text-[11px] text-muted">
              Si cette adresse correspond à un compte du réseau, la personne voit ses horaires dans l'application.
              {current?.linkedUser ? ` Lié au compte de ${current.linkedUser.name}.` : ""}
            </div>
          </div>
          <div>
            <FieldLabel>Téléphone</FieldLabel>
            <input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <FieldLabel>Couleur</FieldLabel>
            <div className="flex gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={c}
                  onClick={() => setForm({ ...form, color: c })}
                  className="h-7 w-7 rounded-full border-2"
                  style={{ background: c, borderColor: form.color === c ? "var(--ink)" : "transparent" }}
                />
              ))}
            </div>
          </div>

          {current && (
            <div className="rounded-card-sm bg-page p-4">
              <div className="mb-2 text-[12.5px] font-bold">Absences</div>
              {myAbsences.length === 0 && <div className="text-[12px] text-muted">Aucune absence sur la période affichée.</div>}
              {myAbsences.map((a) => (
                <div key={a.id} className="flex items-center gap-2 py-1 text-[12.5px]">
                  <Pill className="bg-warn-bg text-warn">{ABSENCE_TYPE_LABELS[a.type]}</Pill>
                  <span>
                    {a.startDate === a.endDate ? dateShort(a.startDate) : `${dateShort(a.startDate)} → ${dateShort(a.endDate)}`}
                  </span>
                  <div className="flex-1" />
                  <button type="button" className="text-[12px] font-semibold text-danger" onClick={() => void run(() => api.del(`/absences/${a.id}`), false)}>
                    Retirer
                  </button>
                </div>
              ))}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select className={`${inputClass} w-auto`} value={absence.type} onChange={(e) => setAbsence({ ...absence, type: e.target.value as typeof absence.type })}>
                  {ABSENCE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {ABSENCE_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
                <input type="date" className={`${inputClass} w-auto`} value={absence.startDate} onChange={(e) => setAbsence({ ...absence, startDate: e.target.value })} />
                <input type="date" className={`${inputClass} w-auto`} value={absence.endDate} onChange={(e) => setAbsence({ ...absence, endDate: e.target.value })} />
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => void run(() => api.post("/absences", { staffMemberId: current.id, ...absence }), false)}
                >
                  Ajouter
                </Button>
              </div>
              <div className="mt-1 text-[11px] text-muted">Les créneaux déjà posés sur ces jours sont retirés.</div>
            </div>
          )}

          {error && <ErrorState error={new Error(error)} />}
          <div className="flex-1" />
          <div className="flex gap-[10px]">
            {current && current.active ? (
              <Button
                variant="outline"
                className="flex-1 text-danger"
                disabled={busy}
                onClick={() =>
                  window.confirm(`Retirer ${current.name} de l'équipe ? Ses créneaux à venir seront supprimés.`) &&
                  void run(() => api.del(`/staff/${current.id}`))
                }
              >
                Retirer de l'équipe
              </Button>
            ) : (
              <Button variant="outline" className="flex-1" onClick={() => setEditing(null)}>
                Retour
              </Button>
            )}
            <Button className="flex-1" disabled={busy || !form.name.trim()} onClick={() => void save()}>
              Enregistrer
            </Button>
          </div>
        </>
      )}
    </Drawer>
  );
}
